// Исполним ли текущий набор входов — и какие каналы ещё можно к нему добавить.
//
// Правило одно и приходит с бэкенда: режим отдаёт `channelRequirements`
// («канал → фичи, которых он требует от исполнителя») и `providerFeatureSets`
// («что покрывает каждая строка каталога активных провайдеров»). Набор исполним,
// когда объединение требований заполненных каналов целиком укладывается в ОДИН
// набор — запрос уходит одному провайдеру, а не всем сразу.
//
// Здесь намеренно нет таблицы «провайдер → каналы»: она жила бы второй копией
// доменного правила и разъехалась бы с маршрутизацией при первом же новом
// провайдере.

import type { ChannelRequirement, GenerationChannel, ModeConfig } from "~/shared/data/chat-models";

/** Какие каналы входа сейчас заполнены пользователем. */
export type ChannelFill = Partial<Record<GenerationChannel, boolean>>;

const requirementsOf = (mode: ModeConfig, channel: GenerationChannel): readonly string[] =>
    mode.channelRequirements.find((entry: ChannelRequirement) => entry.channel === channel)?.features ?? [];

/** Фичи, которых требует набор заполненных каналов, — объединение их требований. */
export function requiredFeatures(mode: ModeConfig, fill: ChannelFill): readonly string[] {
    const required = new Set<string>();
    for (const [channel, isFilled] of Object.entries(fill)) {
        if (!isFilled) continue;
        for (const feature of requirementsOf(mode, channel as GenerationChannel)) required.add(feature);
    }
    return [...required];
}

/**
 * Исполнит ли набор фич ОДИН активный провайдер.
 *
 * Пустое требование исполнимо всегда — тот же знак, что у маршрута и цены:
 * фильтр по фичам к запросу без них просто не применяется.
 */
export function isExecutableByOneProvider(mode: ModeConfig, features: readonly string[]): boolean {
    if (features.length === 0) return true;
    return mode.providerFeatureSets.some((set) => features.every((feature) => set.includes(feature)));
}

/** Исполним ли текущий набор заполненных каналов. */
export function isFillExecutable(mode: ModeConfig, fill: ChannelFill): boolean {
    return isExecutableByOneProvider(mode, requiredFeatures(mode, fill));
}

/**
 * Можно ли добавить канал к тому, что уже заполнено.
 *
 * Именно «вместе с заполненным», а не «сам по себе»: канал, исполнимый в
 * одиночку, может оказаться невозможным рядом с другим — ровно это и есть
 * взаимное гашение, которого нельзя увидеть по союзу фич.
 */
export function isChannelAvailable(mode: ModeConfig, fill: ChannelFill, channel: GenerationChannel): boolean {
    return isFillExecutable(mode, { ...fill, [channel]: true });
}
